import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CartesianGrid, Cell, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis, Pie, PieChart, Bar, BarChart, Legend } from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus, TrendingUp, TrendingDown, Activity, CalendarDays, Users, Footprints, Layers, AlertTriangle, Upload, FileSpreadsheet, Check, X, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChartTooltip } from "@/components/dashboard/ChartTooltip";
import { DeltaBadge } from "@/components/exec/KpiTile";
import { CHART, SERIES, fmtInt, fmtPct, fmtMYR, fmtMYRShort, fmtDate, tickStyle } from "@/lib/format";
import { useScope } from "@/lib/scope";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ sparkline */
export const Sparkline = ({ values = [], color = CHART.gold, width = 96, height = 26 }) => {
  const vals = values.map((v) => (v === null || v === undefined ? null : v));
  const nums = vals.filter((v) => v !== null);
  if (nums.length < 2) return <span className="text-[11px] text-muted-foreground/60">—</span>;
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  const span = max - min || 1;
  const step = width / (vals.length - 1);
  const pts = vals.map((v, i) => (v === null ? null : `${(i * step).toFixed(1)},${(height - 2 - ((v - min) / span) * (height - 4)).toFixed(1)}`));
  const segs = [];
  let cur = [];
  pts.forEach((p) => {
    if (p) cur.push(p);
    else if (cur.length) {
      segs.push(cur);
      cur = [];
    }
  });
  if (cur.length) segs.push(cur);
  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden>
      {segs.map((s, i) => <polyline key={i} points={s.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />)}
    </svg>
  );
};

const Trend = ({ d }) => (d === null || d === undefined ? <Minus size={12} className="text-muted-foreground" /> : d > 0 ? <ArrowUpRight size={13} className="text-success" /> : d < 0 ? <ArrowDownRight size={13} className="text-danger" /> : <Minus size={12} className="text-muted-foreground" />);

/* ------------------------------------------------------------------ store leaderboard */
export const StoreTable = ({ stores = [], compareLabel, onPick, mode = "count", testId }) => {
  const { setScope } = useScope();
  const pick = (id) => (onPick ? onPick(id) : setScope(id));
  return (
    <div className="overflow-x-auto scroll-thin -mx-1" data-testid={testId || "store-comparison-table"}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border/60">
            <TableHead className="text-[11px] uppercase tracking-[0.14em] w-8">#</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.14em]">Store</TableHead>
            {mode === "count" ? (
              <>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right">Visitors</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden md:table-cell">{compareLabel || "vs previous"}</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden lg:table-cell">Share</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden lg:table-cell">Walk-in</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden xl:table-cell">Female</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] hidden sm:table-cell">Trend</TableHead>
              </>
            ) : (
              <>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right">Revenue</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden md:table-cell">vs previous</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden lg:table-cell">Conversion</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden lg:table-cell">Basket</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right hidden xl:table-cell">RM / visitor</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.14em] hidden sm:table-cell">POS</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {stores.map((s) => {
            const dim = mode === "count" ? !s.has_data : !s.has_pos || !s.days_with_sales;
            return (
              <TableRow key={s.id} className={cn("border-border/50 hover:bg-muted/20 cursor-pointer", dim && "opacity-60")} onClick={() => pick(s.id)} data-testid={`store-row-${s.id}`}>
                <TableCell className="font-mono text-xs text-muted-foreground">{s.rank}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-[11px] text-muted-foreground">{s.cluster_name}{s.category_label ? ` · ${s.category_label}` : ""}</div>
                </TableCell>
                {mode === "count" ? (
                  <>
                    <TableCell className="text-right font-mono tabular text-sm">{s.has_data ? fmtInt(s.visitors) : <span className="text-[11px] text-muted-foreground">{s.history ? "no data in period" : "awaiting data"}</span>}</TableCell>
                    <TableCell className="text-right hidden md:table-cell"><span className="inline-flex items-center gap-1 justify-end"><Trend d={s.delta} />{s.delta !== null && s.delta !== undefined ? <DeltaBadge delta={s.delta} tone={s.delta > 0 ? "positive" : s.delta < 0 ? "negative" : "neutral"} testId={`store-delta-${s.id}`} /> : <span className="text-[11px] text-muted-foreground">—</span>}</span></TableCell>
                    <TableCell className="text-right font-mono tabular text-xs hidden lg:table-cell">{fmtPct(s.share, 0)}</TableCell>
                    <TableCell className="text-right font-mono tabular text-xs hidden lg:table-cell">{s.walk_in_rate !== null && s.walk_in_rate !== undefined ? fmtPct(s.walk_in_rate) : <span className="text-muted-foreground">n/c</span>}</TableCell>
                    <TableCell className="text-right font-mono tabular text-xs hidden xl:table-cell">{s.female_share !== null && s.female_share !== undefined ? fmtPct(s.female_share, 0) : <span className="text-muted-foreground">n/c</span>}</TableCell>
                    <TableCell className="hidden sm:table-cell"><Sparkline values={s.spark} /></TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-right font-mono tabular text-sm">{s.has_pos && s.days_with_sales ? fmtMYRShort(s.revenue) : <span className="text-[11px] text-muted-foreground">{s.has_pos ? "no sales in period" : "no POS uploaded"}</span>}</TableCell>
                    <TableCell className="text-right hidden md:table-cell">{s.revenue_delta !== null && s.revenue_delta !== undefined ? <DeltaBadge delta={s.revenue_delta} tone={s.revenue_delta > 0 ? "positive" : "negative"} /> : <span className="text-[11px] text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right font-mono tabular text-xs hidden lg:table-cell">{fmtPct(s.conversion)}</TableCell>
                    <TableCell className="text-right font-mono tabular text-xs hidden lg:table-cell">{s.atv ? fmtMYR(s.atv) : "—"}</TableCell>
                    <TableCell className="text-right font-mono tabular text-xs hidden xl:table-cell">{s.spv ? fmtMYR(s.spv, 2) : "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell">{s.has_pos ? <Badge variant="outline" className="text-[10px] border-success/30 bg-success/10 text-success">{s.ledger?.receipts} receipts</Badge> : <Badge variant="outline" className="text-[10px] border-border/70 text-muted-foreground">none</Badge>}</TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

/* ------------------------------------------------------------------ pattern signals */
const KIND_ICON = { trend: TrendingUp, weekday: CalendarDays, holiday: CalendarDays, payday: CalendarDays, event: CalendarDays, anomaly: Activity, volatility: Activity, walkin: Footprints, demographic: Users, cross_store: Layers, sales_corr: Activity, conversion: TrendingUp, atv: TrendingUp, mix: Layers, accuracy: Activity, peak: TrendingUp, season: CalendarDays, revenue: TrendingUp };

export const PatternSignals = ({ patterns = [], limit = 6, testId }) => {
  const [showAll, setShowAll] = useState(false);
  const list = showAll ? patterns : patterns.slice(0, limit);
  if (!patterns.length)
    return <div className="text-sm text-muted-foreground rounded-lg border border-dashed border-border/70 p-4" data-testid="pattern-signals-empty">Not enough closed days in this window to detect patterns yet. Widen the period or wait for more data.</div>;
  return (
    <div className="space-y-2" data-testid={testId || "pattern-signals"}>
      {list.map((p) => {
        const Icon = KIND_ICON[p.kind] || Activity;
        const dir = p.direction === "up" ? TrendingUp : p.direction === "down" ? TrendingDown : null;
        const Dir = dir;
        return (
          <div key={p.id} className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2.5 flex items-start gap-3" data-testid="pattern-signal" data-kind={p.kind}>
            <span className="h-7 w-7 rounded-md bg-gold/12 text-gold flex items-center justify-center shrink-0 mt-0.5"><Icon size={14} /></span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                {p.headline}
                {Dir && <Dir size={13} className={p.direction === "up" ? "text-success" : "text-danger"} />}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{p.detail}</p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {p.chips?.map((c, i) => <span key={i} className="text-[10px] font-mono rounded border border-border/70 bg-background/60 px-1.5 py-0.5 text-muted-foreground">{c}</span>)}
                <span className="ml-auto text-[10px] font-mono text-muted-foreground/70">signal {Math.round(p.strength * 100)}%</span>
              </div>
            </div>
          </div>
        );
      })}
      {patterns.length > limit && (
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setShowAll(!showAll)} data-testid="pattern-signals-toggle">{showAll ? "Show fewer" : `Show all ${patterns.length} signals`}</Button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ correlation scatter */
export const CorrelationScatter = ({ points = [], r, height = 300, testId }) => {
  const stores = useMemo(() => [...new Set(points.map((p) => p.store))], [points]);
  return (
    <div className="chart-frame" style={{ height }} data-testid={testId || "correlation-scatter"}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 10, right: 10, left: -8, bottom: 4 }}>
          <CartesianGrid stroke={CHART.grid} />
          <XAxis type="number" dataKey="visitors" name="Visitors" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} label={{ value: "Visitors / day", position: "insideBottom", offset: -2, fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
          <YAxis type="number" dataKey="revenue" name="Revenue" tick={tickStyle} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
          <ZAxis range={[50, 50]} />
          <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<ChartTooltip labelFormatter={(l, p) => `${p?.[0]?.payload?.store} · ${fmtDate(p?.[0]?.payload?.date)}${p?.[0]?.payload?.is_weekend ? " · weekend" : ""}`} formatter={(v, name) => [name === "Revenue" ? fmtMYR(v) : fmtInt(v), name]} />} />
          {stores.map((s, i) => (
            <Scatter key={s} name={s} data={points.filter((p) => p.store === s)} fill={SERIES[i % SERIES.length]} fillOpacity={0.7} stroke={SERIES[i % SERIES.length]} isAnimationActive={false} />
          ))}
          {stores.length > 1 && <Legend verticalAlign="top" height={24} />}
        </ScatterChart>
      </ResponsiveContainer>
      {r !== null && r !== undefined && (
        <div className="text-[11px] font-mono text-muted-foreground mt-1 px-1" data-testid="correlation-r">Pearson r {r > 0 ? "+" : ""}{r} · {Math.abs(r) >= 0.6 ? "strong" : Math.abs(r) >= 0.3 ? "moderate" : "weak"} {r >= 0 ? "positive" : "negative"} link between daily visitors and revenue</div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ mix donut + stacked weekly */
export const MixDonut = ({ categories = [], height = 240, testId }) => (
  <div className="flex flex-col sm:flex-row items-center gap-4" data-testid={testId || "mix-donut"}>
    <div style={{ height, width: "100%", maxWidth: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={categories} dataKey="value" nameKey="category" innerRadius="58%" outerRadius="85%" paddingAngle={2} stroke="hsl(var(--card))" isAnimationActive={false}>
            {categories.map((c, i) => <Cell key={c.category} fill={SERIES[i % SERIES.length]} />)}
          </Pie>
          <Tooltip content={<ChartTooltip formatter={(v, name, p) => [`${fmtMYR(v)} · ${fmtPct(p.payload.share, 0)}`, name]} />} />
        </PieChart>
      </ResponsiveContainer>
    </div>
    <ul className="flex-1 w-full space-y-1.5 text-sm">
      {categories.slice(0, 8).map((c, i) => (
        <li key={c.category} className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: SERIES[i % SERIES.length] }} />
          <span className="flex-1 truncate">{c.category}</span>
          <span className="font-mono tabular text-xs text-muted-foreground">{fmtPct(c.share, 0)}</span>
          <span className="font-mono tabular text-xs w-24 text-right">{fmtMYRShort(c.value)}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const MixWeeks = ({ weeks = [], categories = [], height = 260, testId }) => {
  const cats = categories.slice(0, 6).map((c) => c.category);
  const data = weeks.map((w) => ({ label: w.label, ...Object.fromEntries(cats.map((c) => [c, w.shares?.[c] ?? 0])) }));
  return (
    <div className="chart-frame" style={{ height }} data-testid={testId || "mix-weeks-chart"}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 8, left: -14, bottom: 0 }} barCategoryGap={14}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          <XAxis dataKey="label" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} />
          <YAxis tick={tickStyle} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
          <Tooltip content={<ChartTooltip labelFormatter={(l) => `Week of ${l}`} formatter={(v, name) => [fmtPct(v, 0), name]} />} cursor={{ fill: "hsl(var(--gold) / 0.06)" }} />
          {cats.map((c, i) => <Bar key={c} dataKey={c} stackId="mix" fill={SERIES[i % SERIES.length]} isAnimationActive={false} radius={i === cats.length - 1 ? [4, 4, 0, 0] : 0} />)}
          <Legend verticalAlign="top" height={28} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ------------------------------------------------------------------ upload dropzone (admin) */
export const UploadDropzone = ({ onFile, busy, accept = ".xlsx,.xlsm,.csv", label = "Drop the POS Daily Sales Item report here", hint = "Excel (.xlsx) exported from the POS. Monthly reports accumulate - a new file only replaces the dates it covers.", testId }) => {
  const [over, setOver] = useState(false);
  const handle = (files) => {
    const f = files?.[0];
    if (f) onFile(f);
  };
  return (
    <label
      data-testid={testId || "pos-upload-dropzone"}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        handle(e.dataTransfer.files);
      }}
      className={cn("flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 text-center cursor-pointer transition-colors duration-200", over ? "border-gold/50 bg-gold/8" : "border-border/70 bg-muted/10 hover:bg-muted/20", busy && "shimmer pointer-events-none")}
    >
      <input type="file" accept={accept} className="sr-only" onChange={(e) => handle(e.target.files)} data-testid="pos-upload-file-input" disabled={busy} />
      <span className="h-10 w-10 rounded-lg bg-gold/12 text-gold flex items-center justify-center">{busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}</span>
      <div className="text-sm font-medium">{busy ? "Reading the report…" : label}</div>
      <div className="text-xs text-muted-foreground max-w-sm leading-relaxed">{hint}</div>
      <span className="text-[11px] text-gold inline-flex items-center gap-1"><FileSpreadsheet size={12} /> or click to choose a file</span>
    </label>
  );
};

export const ScopeLink = ({ to, children, className, ...rest }) => (
  <Link to={to} className={cn("text-xs text-gold hover:underline inline-flex items-center gap-1 focus-gold rounded", className)} {...rest}>{children} <ArrowUpRight size={12} /></Link>
);

export const DataNote = ({ coverage, period }) => {
  if (!coverage) return null;
  const notes = [];
  if (coverage.open_day) notes.push(`${fmtDate(coverage.open_day.date)} is ${coverage.open_day.reason} (${fmtInt(coverage.open_day.entered)} so far) - excluded from totals and comparisons until the day closes.`);
  if (coverage.missing_days > 0 && !coverage.open_day) notes.push(`${coverage.missing_days} of ${coverage.days_in_period} days have no counts.`);
  else if (coverage.missing_days > 1) notes.push(`${coverage.missing_days - 1} other day(s) have no counts.`);
  if (coverage.stores_total > 1 && coverage.stores_with_data < coverage.stores_total) notes.push(`${coverage.stores_total - coverage.stores_with_data} of ${coverage.stores_total} stores have no data in this window - group figures cover the reporting stores only.`);
  if (coverage.feeds && coverage.feeds.count && !coverage.feeds.wir) notes.push("Passing-by (walk-in rate) was not captured in this window.");
  if (!notes.length) return null;
  return (
    <div className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2 text-[11px] text-muted-foreground flex items-start gap-2" data-testid="data-note">
      <AlertTriangle size={12} className="text-warning mt-0.5 shrink-0" />
      <span>{notes.join(" ")}{period?.compare_label ? ` Comparison: ${period.compare_label}.` : ""}</span>
    </div>
  );
};

export const Pill = ({ ok, children }) => (
  <span className={cn("inline-flex items-center gap-1 text-[11px] font-mono rounded-md border px-2 py-0.5", ok ? "border-success/30 bg-success/10 text-success" : "border-border/70 bg-muted/20 text-muted-foreground")}>{ok ? <Check size={11} /> : <X size={11} />}{children}</span>
);
